import { useEffect, useMemo, useRef, useState } from 'react';
import { addDoc, collection, deleteDoc, doc, onSnapshot, query, serverTimestamp, updateDoc, where } from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { db, storage } from '../../firebase';
import ExpandedNoticeModal from '../format-converter/components/ExpandedNoticeModal';
import NoticeCenter from '../format-converter/components/NoticeCenter';
import { truncateFileName } from '../format-converter/utils';
import StorageGridView from './components/StorageGridView';
import StorageDeleteConfirmModal from './components/StorageDeleteConfirmModal';
import StorageItemActionsMenu from './components/StorageItemActionsMenu';
import StorageListView from './components/StorageListView';
import StoragePropertiesModal from './components/StoragePropertiesModal';
import StorageToolbar from './components/StorageToolbar';
import { ROOT_FOLDER_ID, sortStorageItems } from './utils';

function StorageProject({ user: currentUser = null }) {
  const [items, setItems] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(ROOT_FOLDER_ID);
  const [queryText, setQueryText] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('updated-desc');
  const [viewMode, setViewMode] = useState('grid');
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [openItemMenuId, setOpenItemMenuId] = useState('');
  const [expandedNotice, setExpandedNotice] = useState(null);
  const [noticeFeed, setNoticeFeed] = useState([]);
  const [activeNoticeKey, setActiveNoticeKey] = useState('');
  const [isNoticeListOpen, setIsNoticeListOpen] = useState(false);
  const [propertiesItem, setPropertiesItem] = useState(null);
  const [deleteCandidate, setDeleteCandidate] = useState(null);

  const fileInputRef = useRef(null);
  const addMenuRef = useRef(null);
  const itemMenuRef = useRef(null);

  const showFloatingNotice = (message, tone = 'info', key = '') => {
    if (!message) return;
    const noticeKey = key || `general:${tone}`;
    const title = noticeKey.startsWith('item:') ? 'Storage item' : 'Storage';
    setNoticeFeed((prev) => {
      const now = Date.now();
      const next = [...prev];
      const index = next.findIndex((entry) => entry.key === noticeKey);
      const notice = { key: noticeKey, tone, title, message, updatedAt: now };
      if (index >= 0) next[index] = notice;
      else next.push(notice);
      next.sort((a, b) => b.updatedAt - a.updatedAt);
      return next.slice(0, 10);
    });
    setActiveNoticeKey(noticeKey);
  };

  useEffect(() => {
    if (!currentUser) {
      setItems([]);
      return undefined;
    }

    const itemsQuery = query(collection(db, 'storageItems'), where('uid', '==', currentUser.uid));
    const unsub = onSnapshot(
      itemsQuery,
      (snapshot) => {
        setItems(snapshot.docs.map((snapshotDoc) => ({ id: snapshotDoc.id, ...snapshotDoc.data() })));
      },
      (error) => {
        showFloatingNotice(error.message || 'Could not load storage items.', 'error');
      }
    );
    return () => unsub();
  }, [currentUser]);

  useEffect(() => {
    if (!noticeFeed.length) {
      setActiveNoticeKey('');
      setIsNoticeListOpen(false);
      return;
    }
    if (!noticeFeed.some((entry) => entry.key === activeNoticeKey)) {
      setActiveNoticeKey(noticeFeed[0].key);
    }
  }, [noticeFeed, activeNoticeKey]);

  useEffect(() => {
    const onDocumentClick = (event) => {
      if (isAddMenuOpen && addMenuRef.current && !addMenuRef.current.contains(event.target)) {
        setIsAddMenuOpen(false);
      }
      if (openItemMenuId && itemMenuRef.current && !itemMenuRef.current.contains(event.target)) {
        setOpenItemMenuId('');
      }
    };
    document.addEventListener('mousedown', onDocumentClick);
    return () => document.removeEventListener('mousedown', onDocumentClick);
  }, [isAddMenuOpen, openItemMenuId]);

  const foldersById = useMemo(() => {
    const map = new Map();
    items.forEach((item) => {
      if (item.type === 'folder') map.set(item.id, item);
    });
    return map;
  }, [items]);

  useEffect(() => {
    if (!currentFolderId) return;
    if (!foldersById.has(currentFolderId)) {
      setCurrentFolderId(ROOT_FOLDER_ID);
    }
  }, [currentFolderId, foldersById]);

  const visibleItems = useMemo(() => {
    const lowerSearch = queryText.trim().toLowerCase();
    const inFolder = items.filter((item) => (item.parentId || ROOT_FOLDER_ID) === currentFolderId);

    const filtered = inFolder.filter((item) => {
      if (typeFilter !== 'all' && item.type !== typeFilter) return false;
      if (!lowerSearch) return true;
      return String(item.name || '').toLowerCase().includes(lowerSearch);
    });

    return sortStorageItems(filtered, sortBy);
  }, [items, currentFolderId, typeFilter, queryText, sortBy]);

  const currentFolderName = useMemo(() => {
    if (!currentFolderId) return 'My files';
    return foldersById.get(currentFolderId)?.name || 'Folder';
  }, [currentFolderId, foldersById]);

  const createFolder = async () => {
    if (!currentUser) return;
    const folderName = window.prompt('Folder name');
    const normalized = String(folderName || '').trim();
    if (!normalized) return;

    try {
      await addDoc(collection(db, 'storageItems'), {
        uid: currentUser.uid,
        type: 'folder',
        name: normalized,
        parentId: currentFolderId,
        size: 0,
        contentType: '',
        storagePath: '',
        downloadURL: '',
        status: 'ready',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      showFloatingNotice('Folder created.', 'success');
    } catch (error) {
      showFloatingNotice(error.message || 'Could not create folder.', 'error');
    }
  };

  const uploadFiles = async (fileList) => {
    if (!currentUser) return;
    const files = Array.from(fileList || []);
    if (!files.length) return;

    setIsUploading(true);
    showFloatingNotice(`Uploading ${files.length} file${files.length > 1 ? 's' : ''}...`, 'info');

    let successCount = 0;
    for (const candidate of files) {
      const safeName = String(candidate.name || 'file').replace(/[^\w.\-]+/g, '_');
      const itemRef = await addDoc(collection(db, 'storageItems'), {
        uid: currentUser.uid,
        type: 'file',
        name: candidate.name || safeName,
        parentId: currentFolderId,
        size: Number(candidate.size || 0),
        contentType: candidate.type || 'application/octet-stream',
        storagePath: '',
        downloadURL: '',
        status: 'uploading',
        progress: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      try {
        const storagePath = `uploads/${currentUser.uid}/storage/${itemRef.id}-${safeName}`;
        const fileRef = ref(storage, storagePath);

        await new Promise((resolve, reject) => {
          const task = uploadBytesResumable(fileRef, candidate);
          task.on(
            'state_changed',
            (snapshot) => {
              const pct = Math.max(0, Math.min(100, Math.floor((snapshot.bytesTransferred / snapshot.totalBytes) * 100)));
              updateDoc(doc(db, 'storageItems', itemRef.id), { progress: pct, updatedAt: serverTimestamp() }).catch(() => {});
            },
            reject,
            resolve
          );
        });

        const downloadURL = await getDownloadURL(fileRef);
        await updateDoc(doc(db, 'storageItems', itemRef.id), {
          storagePath,
          downloadURL,
          status: 'ready',
          progress: 100,
          updatedAt: serverTimestamp()
        });
        successCount += 1;
      } catch (error) {
        await updateDoc(doc(db, 'storageItems', itemRef.id), {
          status: 'failed',
          error: error.message || 'Upload failed.',
          updatedAt: serverTimestamp()
        });
      }
    }

    setIsUploading(false);
    if (successCount > 0) {
      showFloatingNotice(`Uploaded ${successCount} file${successCount > 1 ? 's' : ''}.`, 'success');
    }
  };

  const handleUploadInputChange = (event) => {
    uploadFiles(event.target.files).catch((error) => {
      showFloatingNotice(error.message || 'Upload failed.', 'error');
      setIsUploading(false);
    });
    event.target.value = '';
  };

  const downloadItem = (item) => {
    if (!item?.downloadURL) return;
    const filename = item.name || 'download';
    const link = document.createElement('a');
    link.href = item.downloadURL;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const renameItem = async (item) => {
    if (!item?.id) return;
    const nextName = window.prompt('Rename item', item.name || '');
    const normalized = String(nextName || '').trim();
    if (!normalized || normalized === item.name) return;
    try {
      await updateDoc(doc(db, 'storageItems', item.id), { name: normalized, updatedAt: serverTimestamp() });
      showFloatingNotice('Item renamed.', 'success', `item:${item.id}`);
    } catch (error) {
      showFloatingNotice(error.message || 'Could not rename item.', 'error', `item:${item.id}`);
    }
  };

  const deleteItem = async (item) => {
    if (!item?.id) return;

    try {
      if (item.type === 'folder') {
        const hasChildren = items.some((candidate) => (candidate.parentId || ROOT_FOLDER_ID) === item.id);
        if (hasChildren) {
          showFloatingNotice('Folder is not empty.', 'error', `item:${item.id}`);
          return;
        }
      } else if (item.storagePath) {
        await deleteObject(ref(storage, item.storagePath));
      }

      await deleteDoc(doc(db, 'storageItems', item.id));
      showFloatingNotice('Item deleted.', 'success', `item:${item.id}`);
    } catch (error) {
      showFloatingNotice(error.message || 'Could not delete item.', 'error', `item:${item.id}`);
    }
  };

  const renderItemActions = (item) => (
    <StorageItemActionsMenu
      item={item}
      isOpen={openItemMenuId === item.id}
      setOpenItemMenuId={setOpenItemMenuId}
      itemMenuRef={itemMenuRef}
      onDownload={downloadItem}
      onRename={(nextItem) => {
        renameItem(nextItem).catch(() => {});
      }}
      onProperties={setPropertiesItem}
      onDelete={(nextItem) => {
        setDeleteCandidate(nextItem);
      }}
    />
  );

  if (!currentUser) return null;
  const activeNotice = noticeFeed.find((entry) => entry.key === activeNoticeKey) || noticeFeed[0] || null;

  return (
    <main className="relative text-slate-900">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
        <section className="rounded-3xl border border-white/60 bg-white/85 p-6 shadow-xl shadow-brand-100/40 backdrop-blur">
          <StorageToolbar
            queryText={queryText}
            setQueryText={setQueryText}
            typeFilter={typeFilter}
            setTypeFilter={setTypeFilter}
            sortBy={sortBy}
            setSortBy={setSortBy}
            viewMode={viewMode}
            setViewMode={setViewMode}
            currentFolderId={currentFolderId}
            setCurrentFolderId={setCurrentFolderId}
            rootFolderId={ROOT_FOLDER_ID}
            isAddMenuOpen={isAddMenuOpen}
            setIsAddMenuOpen={setIsAddMenuOpen}
            addMenuRef={addMenuRef}
            fileInputRef={fileInputRef}
            onUploadClick={() => fileInputRef.current?.click()}
            onUploadInputChange={handleUploadInputChange}
            onCreateFolder={() => {
              createFolder().catch(() => {});
            }}
          />
        </section>

        <section className="rounded-3xl border border-white/60 bg-white/85 p-6 shadow-xl shadow-brand-100/40 backdrop-blur">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-bold">{truncateFileName(currentFolderName, 36)}</h2>
            <p className="text-xs text-slate-500">
              {visibleItems.length} item(s){isUploading ? ' • uploading...' : ''}
            </p>
          </div>

          {viewMode === 'grid' ? (
            <StorageGridView items={visibleItems} onOpenFolder={setCurrentFolderId} renderItemActions={renderItemActions} />
          ) : (
            <StorageListView items={visibleItems} onOpenFolder={setCurrentFolderId} renderItemActions={renderItemActions} />
          )}
        </section>
      </div>

      <NoticeCenter
        activeNotice={activeNotice}
        noticeFeed={noticeFeed}
        isNoticeListOpen={isNoticeListOpen}
        setIsNoticeListOpen={setIsNoticeListOpen}
        setActiveNoticeKey={setActiveNoticeKey}
        setExpandedNotice={setExpandedNotice}
      />
      <ExpandedNoticeModal expandedNotice={expandedNotice} onClose={() => setExpandedNotice(null)} />
      <StoragePropertiesModal item={propertiesItem} onClose={() => setPropertiesItem(null)} />
      <StorageDeleteConfirmModal
        item={deleteCandidate}
        onCancel={() => setDeleteCandidate(null)}
        onConfirm={(item) => {
          deleteItem(item)
            .catch(() => {})
            .finally(() => setDeleteCandidate(null));
        }}
      />
    </main>
  );
}

export default StorageProject;
